/**
 * Analytics Helper Functions
 * Centralized functions for building event properties
 */

import type {
  OnboardingSignupProperties,
  OnboardingInfoGatheringProperties,
  OnboardingSocialMediaManualProperties,
  OnboardingNicheManualProperties,
  OnboardingProfileBuilderProperties,
  OnboardingProfileAppealProperties,
  DashboardEventProperties,
  LogoutEventProperties,
  ProfileShareEventProperties,
  CopyLinkEventProperties,
  ProfileRedirectionEventProperties,
  SeoEditEventProperties,
  ShareProfileEditEventProperties,
  JobShareEventProperties,
  AudienceSocialMediaClickProperties,
  AudienceJobEventProperties,
  AudienceJobSearchProperties,
  AudienceJobFilterProperties,
  AudienceEarnClickProperties,
  AudienceIncognitoRestrictionProperties,
  AudienceJobAlertEmailSubmittedProperties,
  AudienceJobAlertPrefsSubmittedProperties,
  AudienceJobAlertUnsubscribedProperties,
  DeleteAccountEventProperties,
  LinkAccountEventProperties,
  EditProfileEventProperties,
  EditProfileNameEventProperties,
  EditProfileNicheEventProperties,
  EditProfileSocialLinkEventProperties,
  DesignEditEventProperties,
  DesignChangeType,
  SettingsNicheEventProperties,
  SettingsKeywordEventProperties,
  SettingsCountryEventProperties,
  SettingsIndustryEventProperties,
  FilterJobsEventProperties,
  FilterCompanyEventProperties,
  EditProfileLinkEventProperties,
  EarningEventProperties,
  PayoutDownloadEventProperties,
  PayoutAddedEventProperties,
  RevenueEventProperties,
  FirstJobApplicationEventProperties,
  ShareType,
  SourceScreen,
  PostStudioEventProperties,
  PostStudioPlatformEventProperties,
} from "@artha/types";
import type { JobItemDto } from "@artha/api-contracts";
import type { JobFiltersState } from "@artha/ui";

/**
 * Common user properties interface
 */
export interface CommonUserInfo {
  username?: string;
  name?: string;
  user_id?: string;
  email?: string;
  primary_social_account?: string;
  followers?: number;
}

const normalizeUsername = (value?: string) => {
  if (!value) return undefined;
  return value.startsWith("@") ? value.slice(1) : value;
};

/**
 * Get common user properties from creator object
 * Single source of truth for user properties
 */
export function getCommonUserInfo(creator?: {
  id?: string;
  displayName?: string;
  username?: string;
  email?: string;
  primaryPlatform?: string;
  socialAccounts?: Array<{ handle?: string; provider?: string }>;
  totalFollowers?: number;
  profile?: {
    displayName?: string;
    username?: string;
    primaryPlatform?: string;
  };
  auth?: {
    email?: string;
  };
  metrics?: {
    totalFollowers?: number;
  };
}): CommonUserInfo {
  if (!creator) return {};

  const email = creator.email || creator.auth?.email;
  const username =
    normalizeUsername(creator.username) ||
    normalizeUsername(creator.profile?.username) ||
    normalizeUsername(creator.socialAccounts?.[0]?.handle);

  return {
    username,
    name: creator.displayName || creator.profile?.displayName,
    user_id: email,
    email: email,
    primary_social_account:
      creator.primaryPlatform ||
      creator.profile?.primaryPlatform ||
      creator.socialAccounts?.[0]?.provider,
    followers: creator.totalFollowers || creator.metrics?.totalFollowers,
  };
}

/**
 * Build CR_SIGNUP event properties
 */
export function buildSignupEventProps(
  socialMedia: string,
  creator?: any
): OnboardingSignupProperties {
  return {
    social_media: socialMedia,
    ...getCommonUserInfo(creator),
  };
}

/**
 * Build CR_INFO_GATHERING event properties
 */
export function buildInfoGatheringEventProps(
  statuses?: {
    creator_profile?: "SUCCESS" | "FAIL";
    creator_niche?: "SUCCESS" | "FAIL";
    creator_jobs?: "SUCCESS" | "FAIL";
  },
  creator?: any
): OnboardingInfoGatheringProperties {
  return {
    ...statuses,
    ...getCommonUserInfo(creator),
  };
}

/**
 * Build CR_SOCIALMEDIA_MANUAL event properties
 */
export function buildSocialMediaManualEventProps(
  data: {
    social_media: string;
    social_media_handle?: string;
    social_media_url?: string;
  },
  creator?: any
): OnboardingSocialMediaManualProperties {
  return {
    ...data,
    ...getCommonUserInfo(creator),
  };
}

/**
 * Build CR_NICHE_MANUAL event properties
 */
export function buildNicheManualEventProps(
  data: {
    primary_niche: string;
    secondary_niche?: string[];
    keywords?: string[];
  },
  creator?: any
): OnboardingNicheManualProperties {
  return {
    ...data,
    ...getCommonUserInfo(creator),
  };
}

/**
 * Build CR_PROFILE_BUILDER event properties
 */
export function buildProfileBuilderEventProps(
  data: {
    status: "approved" | "rejected" | "waitlist" | "pending_review";
    artha_link?: string;
    primary_niche?: string;
    secondary_niche?: string[];
    keywords?: string[];
    username?: string;
  },
  creator?: any
): OnboardingProfileBuilderProperties {
  return {
    ...getCommonUserInfo(creator),
    ...data,
  };
}

/**
 * Build CR_PROFILE_APPEAL event properties
 */
export function buildProfileAppealEventProps(
  data: {
    rejection_reason?: string;
    appeal_message?: string;
  },
  creator?: any
): OnboardingProfileAppealProperties {
  return {
    ...data,
    ...getCommonUserInfo(creator),
  };
}


// ============================================
// Creator Dashboard & Navigation Event Builders
// ============================================

/**
 * Build CR_DASHBOARD event properties
 */
export function buildDashboardEventProps(
  creator?: any
): DashboardEventProperties {
  return {
    ...getCommonUserInfo(creator),
  };
}

/**
 * Build onboarding checklist event properties.
 */
export function buildCreatorOnboardingEventProps(
  creator?: any
): DashboardEventProperties {
  return {
    ...getCommonUserInfo(creator),
  };
}

/**
 * Build CR_LOGOUT event properties
 */
export function buildLogoutEventProps(
  sessionDuration?: number,
  creator?: any
): LogoutEventProperties {
  return {
    session_duration: sessionDuration,
    ...getCommonUserInfo(creator),
  };
}

// ============================================
// Creator Profile Sharing Event Builders
// ============================================

/**
 * Build CR_FIRST_PROFILE_SHARE / CR_PROFILE_SHARE event properties
 */
export function buildProfileShareEventProps(
  data: {
    share_type: ShareType;
    source_screen: SourceScreen;
  },
  creator?: any
): ProfileShareEventProperties {
  return {
    ...data,
    ...getCommonUserInfo(creator),
  };
}

/**
 * Build CR_SHARE_PROFILE_COPYLINK event properties
 */
export function buildCopyLinkEventProps(
  sourceScreen: SourceScreen,
  creator?: any
): CopyLinkEventProperties {
  return {
    source_screen: sourceScreen,
    ...getCommonUserInfo(creator),
  };
}

/**
 * Build CR_SHARE_PROFILE_REDIRECTION event properties
 */
export function buildProfileRedirectionEventProps(
  linkedAccount: string,
  creator?: any
): ProfileRedirectionEventProperties {
  return {
    linked_account: linkedAccount,
    ...getCommonUserInfo(creator),
  };
}

/**
 * Build CR_SEO_EDIT event properties
 */
export function buildSeoEditEventProps(
  settings?: string,
  creator?: any
): SeoEditEventProperties {
  return {
    settings,
    ...getCommonUserInfo(creator),
  };
}

/**
 * Build CR_SHARE_PROFILE_EDIT event properties
 */
export function buildShareProfileEditEventProps(
  creator?: any
): ShareProfileEditEventProperties {
  return {
    ...getCommonUserInfo(creator),
  };
}

// ============================================
// Creator Job Sharing Event Builders
// ============================================

/**
 * Build CR_SHARE_FIRST_JOB / CR_SHARE_JOB event properties
 */
export function buildJobShareEventProps(
  data: {
    social_media: string;
    job_id: string;
    job_title: string;
    job_company: string;
    job_cpa_value?: number;
    job_cpc_value?: number;
  },
  creator?: any
): JobShareEventProperties {
  return {
    ...data,
    ...getCommonUserInfo(creator),
  };
}

// ============================================
// Creator Account Management Event Builders
// ============================================

/**
 * Build CR_DELETE_ACCOUNT event properties
 */
export function buildDeleteAccountEventProps(
  reason?: string,
  creator?: any
): DeleteAccountEventProperties {
  return {
    reason,
    ...getCommonUserInfo(creator),
  };
}

/**
 * Build CR_LINK_ACCOUNT event properties
 */
export function buildLinkAccountEventProps(
  data: {
    social_media: string;
    source_screen: SourceScreen;
    linked_accounts: string[];
  },
  creator?: any
): LinkAccountEventProperties {
  return {
    ...data,
    ...getCommonUserInfo(creator),
  };
}

// ============================================
// Audience Event Builders
// ============================================

const getJobRevenueProps = (job: JobItemDto): Pick<AudienceJobEventProperties, "job_value" | "revenue_type"> => {
  if (typeof job.cpa_value === "number") {
    return { job_value: job.cpa_value, revenue_type: "RPA" };
  }
  if (typeof job.cpc_value === "number") {
    return { job_value: job.cpc_value, revenue_type: "RPC" };
  }
  if (typeof job.rph_value === "number") {
    return { job_value: job.rph_value, revenue_type: "RPH" };
  }
  return {};
};

export function buildAudienceSocialMediaClickProps(
  socialMedia: string
): AudienceSocialMediaClickProperties {
  return {
    social_media: socialMedia,
  };
}

export function buildAudienceJobProps(job: JobItemDto): AudienceJobEventProperties {
  return {
    job_id: job.id,
    ...(job.ref_id && { job_ref_id: job.ref_id }),
    job_title: job.title,
    job_company: job.company,
    ...getJobRevenueProps(job),
  };
}

export function buildAudienceVpnRestrictionProps(
  ipTimezone: string,
  browserTimezone: string,
  job?: JobItemDto | null,
  creator?: any
): any {
  return {
    job_id: job?.id,
    job_title: job?.title,
    job_company: job?.company,
    ip_timezone: ipTimezone,
    browser_timezone: browserTimezone,
    ...getCommonUserInfo(creator),
    // Ensure creator info is mapped to restriction-specific fields if needed
    creator_id: creator?.id,
    creator_username: creator?.username,
  };
}

export function buildAudienceCountryRestrictionProps(
  seekerCountry: string,
  jobCountry: string,
  job: JobItemDto,
  creator?: any
): any {
  return {
    job_id: job.id,
    job_title: job.title,
    job_company: job.company,
    seeker_country: seekerCountry,
    job_country: jobCountry,
    ...getCommonUserInfo(creator),
    creator_id: creator?.id,
    creator_username: creator?.username,
  };
}

export function buildAudienceIncognitoRestrictionProps(
  job?: JobItemDto | null,
  creator?: any
): AudienceIncognitoRestrictionProperties {
  return {
    job_id: job?.id,
    job_title: job?.title,
    job_company: job?.company,
    ...getCommonUserInfo(creator),
    creator_id: creator?.id,
    creator_username: creator?.username,
  };
}

export function buildAudienceJobSearchProps(
  query: string
): AudienceJobSearchProperties {
  return {
    job_search: query,
  };
}

const FILTER_KEYS: Array<keyof JobFiltersState> = [
  "categories",
  "jobTypes",
  "experienceLevels",
  "educationLevels",
  "workModes",
  "country",
  "state",
  "city",
  "salaryRanges",
  "companies",
  "industries",
  "companyRating",
];

const areStringArraysEqual = (a: string[], b: string[]) => {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((value, index) => value === sortedB[index]);
};

export const getChangedFilterKeys = (
  previous: JobFiltersState,
  next: JobFiltersState
): string[] =>
  FILTER_KEYS.filter((key) => {
    const prevValue = previous[key];
    const nextValue = next[key];
    if (Array.isArray(prevValue) && Array.isArray(nextValue)) {
      return !areStringArraysEqual(prevValue, nextValue);
    }
    return prevValue !== nextValue;
  });

export const getFiltersAppliedCount = (state: JobFiltersState): number => {
  const arrayCounts =
    state.categories.length +
    state.jobTypes.length +
    state.experienceLevels.length +
    state.educationLevels.length +
    state.workModes.length +
    state.salaryRanges.length +
    state.companies.length +
    state.industries.length;
  const locationCount =
    (state.country ? 1 : 0) + (state.state ? 1 : 0) + (state.city ? 1 : 0);
  const ratingCount = state.companyRating > 0 ? 1 : 0;
  return arrayCounts + locationCount + ratingCount;
};

export function buildAudienceJobFilterProps(
  filtersChanged: string[],
  filtersAppliedCount: number
): AudienceJobFilterProperties {
  return {
    filters_changed: filtersChanged,
    filters_applied_count: filtersAppliedCount,
  };
}

export function buildAudienceEarnClickProps(
  username: string
): AudienceEarnClickProperties {
  const normalized = username.startsWith("@") ? username.slice(1) : username;
  const handle = normalized ? `@${normalized}` : "@";
  return {
    ref_username: normalized,
    ref_username_with_at: handle,
  };
}

export function buildAudienceJobAlertEmailSubmittedProps(
  email: string
): AudienceJobAlertEmailSubmittedProperties {
  return {
    email,
  };
}

export function buildAudienceJobAlertPrefsSubmittedProps(
  email: string,
  frequency: string,
  experience: string
): AudienceJobAlertPrefsSubmittedProperties {
  return {
    email,
    frequency,
    experience,
  };
}

export function buildAudienceJobAlertUnsubscribedProps(
  creatorUsername: string,
  subscriptionExternalId: string,
  remainingCreatorAlerts?: number
): AudienceJobAlertUnsubscribedProperties {
  return {
    creator_username: normalizeUsername(creatorUsername) || creatorUsername,
    subscription_external_id: subscriptionExternalId,
    remaining_creator_alerts: remainingCreatorAlerts,
  };
}

// ============================================
// My Link - Profile Edit Event Builders
// ============================================

/**
 * Build CR_EDIT_PROFILE event properties
 */
export function buildEditProfileEventProps(
  creator?: any
): EditProfileEventProperties {
  return {
    ...getCommonUserInfo(creator),
  };
}

/**
 * Build CR_EDIT_PROFILE_PIC event properties
 */
export function buildEditProfilePicEventProps(
  creator?: any
): EditProfileEventProperties {
  return {
    ...getCommonUserInfo(creator),
  };
}

/**
 * Build CR_EDIT_PROFILE_NAME event properties
 */
export function buildEditProfileNameEventProps(
  newName: string,
  creator?: any
): EditProfileNameEventProperties {
  return {
    ...getCommonUserInfo(creator),
    name: newName,
  };
}

/**
 * Build CR_EDIT_PROFILE_BIO event properties
 */
export function buildEditProfileBioEventProps(
  creator?: any
): EditProfileEventProperties {
  return {
    ...getCommonUserInfo(creator),
  };
}

/**
 * Build CR_EDIT_PROFILE_NICHE event properties
 */
export function buildEditProfileNicheEventProps(
  niches: string[],
  creator?: any
): EditProfileNicheEventProperties {
  return {
    primary_niche: niches[0],
    secondary_niche: niches.slice(1),
    ...getCommonUserInfo(creator),
  };
}

/**
 * Build CR_EDIT_PROFILE_SOCIALLINK event properties
 */
export function buildEditProfileSocialLinkEventProps(
  socialMedia: string,
  creator?: any
): EditProfileSocialLinkEventProperties {
  return {
    social_media: socialMedia,
    ...getCommonUserInfo(creator),
  };
}

// ============================================
// My Link - Design Event Builders
// ============================================

/**
 * Build CR_DESIGN_START event properties
 */
export function buildDesignStartEventProps(
  creator?: any
): EditProfileEventProperties {
  return {
    ...getCommonUserInfo(creator),
  };
}

/**
 * Build CR_DESIGN_EDIT event properties
 */
export function buildDesignEditEventProps(
  designChange: DesignChangeType,
  creator?: any
): DesignEditEventProperties {
  return {
    design_change: designChange,
    ...getCommonUserInfo(creator),
  };
}

// ============================================
// My Link - Settings Event Builders
// ============================================

/**
 * Build CR_SETTINGS event properties
 */
export function buildSettingsEventProps(
  creator?: any
): EditProfileEventProperties {
  return {
    ...getCommonUserInfo(creator),
  };
}

/**
 * Build CR_SETTINGS_NICHE_ADD / CR_SETTINGS_NICHE_REMOVE event properties
 */
export function buildSettingsNicheEventProps(
  niches: string[],
  creator?: any
): SettingsNicheEventProperties {
  return {
    primary_niche: niches[0],
    secondary_niche: niches.slice(1),
    ...getCommonUserInfo(creator),
  };
}

/**
 * Build CR_SETTINGS_JOBKW_ADD / CR_SETTINGS_JOBKW_REMOVE event properties
 */
export function buildSettingsKeywordEventProps(
  keywords: string[],
  creator?: any
): SettingsKeywordEventProperties {
  return {
    keywords,
    ...getCommonUserInfo(creator),
  };
}

/**
 * Build CR_SETTINGS_COUNTRY_ADD / CR_SETTINGS_COUNTRY_REMOVE event properties
 */
export function buildSettingsCountryEventProps(
  country: string,
  creator?: any
): SettingsCountryEventProperties {
  return {
    job_country: country,
    ...getCommonUserInfo(creator),
  };
}

/**
 * Build CR_SETTINGS_EMPIND_ADD / CR_SETTINGS_EMPIND_REMOVE event properties
 */
export function buildSettingsIndustryEventProps(
  industry: string,
  creator?: any
): SettingsIndustryEventProperties {
  return {
    industry,
    ...getCommonUserInfo(creator),
  };
}

/**
 * Build CR_SETTINGS_EMPKW_ADD / CR_SETTINGS_EMPKW_REMOVE event properties
 */
export function buildSettingsEmpKeywordEventProps(
  keywords: string[],
  creator?: any
): SettingsKeywordEventProperties {
  return {
    keywords,
    ...getCommonUserInfo(creator),
  };
}

// ============================================
// My Link - Filter Event Builders
// ============================================

/**
 * Build CR_FILTER event properties
 */
export function buildFilterEventProps(
  creator?: any
): EditProfileEventProperties {
  return {
    ...getCommonUserInfo(creator),
  };
}

/**
 * Build CR_FILTER_JOBS event properties
 */
export function buildFilterJobsEventProps(
  filtersChanged: string[],
  filtersAppliedCount: number,
  creator?: any
): FilterJobsEventProperties {
  return {
    filters_changed: filtersChanged,
    filters_applied_count: filtersAppliedCount,
    ...getCommonUserInfo(creator),
  };
}

/**
 * Build CR_FILTER_COMPANY event properties
 */
export function buildFilterCompanyEventProps(
  filtersChanged: string[],
  filtersAppliedCount: number,
  creator?: any
): FilterCompanyEventProperties {
  return {
    filters_changed: filtersChanged,
    filters_applied_count: filtersAppliedCount,
    ...getCommonUserInfo(creator),
  };
}

// ============================================
// Earnings & Revenue Event Builders
// ============================================

export function buildEditProfileLinkEventProps(
  username: string,
  creator?: any
): EditProfileLinkEventProperties {
  return {
    ...getCommonUserInfo(creator),
    username: normalizeUsername(username) || username,
  };
}

export function buildEarningEventProps(
  creator?: any
): EarningEventProperties {
  return {
    ...getCommonUserInfo(creator),
  };
}

export function buildPayoutDownloadEventProps(
  creator?: any
): PayoutDownloadEventProperties {
  return {
    ...getCommonUserInfo(creator),
  };
}

export function buildPayoutAddedEventProps(
  bankConnected: boolean,
  creator?: any
): PayoutAddedEventProperties {
  return {
    bank_connected: bankConnected,
    ...getCommonUserInfo(creator),
  };
}

export function buildRevenueEventProps(
  data: { lifetime_revenue?: number; weekly_revenue?: number },
  creator?: any
): RevenueEventProperties {
  return {
    ...data,
    ...getCommonUserInfo(creator),
  };
}

export function buildFirstJobApplicationEventProps(
  job: JobItemDto,
  jobsViewedInSession: number,
  creator?: any
): FirstJobApplicationEventProperties {
  return {
    job_id: job.id,
    job_title: job.title,
    job_company: job.company,
    job_cpa_value: typeof job.cpa_value === "number" ? job.cpa_value : undefined,
    job_cpc_value: typeof job.cpc_value === "number" ? job.cpc_value : undefined,
    jobs_viewed_in_session: jobsViewedInSession,
    ...getCommonUserInfo(creator),
  };
}

/**
 * Build CR_POSTSTUDIO event properties
 */
export function buildPostStudioOpenedEventProps(
  creator?: any
): PostStudioEventProperties {
  return {
    ...getCommonUserInfo(creator),
  };
}

/**
 * Build CR_POSTSTUDIO_JOBSSELECT event properties
 */
export function buildPostStudioJobsSelectEventProps(
  creator?: any
): PostStudioEventProperties {
  return {
    ...getCommonUserInfo(creator),
  };
}

/**
 * Build CR_POSTSTUDIO_PLATFORM event properties
 */
export function buildPostStudioPlatformEventProps(
  platform: string,
  creator?: any
): PostStudioPlatformEventProperties {
  return {
    post_channel: platform,
    ...getCommonUserInfo(creator),
  };
}

/**
 * Build CR_POSTSTUDIO_REGENERATE event properties
 */
export function buildPostStudioRegenerateEventProps(
  creator?: any
): PostStudioEventProperties {
  return {
    ...getCommonUserInfo(creator),
  };
}

/**
 * Build CR_POSTSTUDIO_SHARE event properties
 */
export function buildPostStudioShareEventProps(
  creator?: any
): PostStudioEventProperties {
  return {
    ...getCommonUserInfo(creator),
  };
}
